import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Space, Tooltip } from 'antd';
import { MenuOutlined, CloseOutlined, UserOutlined, LogoutOutlined, SettingOutlined, SunOutlined, MoonOutlined, LaptopOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import type { ThemeMode } from '../../contexts/ThemeContext';
import { siteChromeEn } from '../../content/siteChrome.en';
import { getPersona } from '../../utils/persona';
import styles from './Header.module.css';

const themeIcons: Record<ThemeMode, React.ReactNode> = {
  light: <SunOutlined />,
  dark: <MoonOutlined />,
  system: <LaptopOutlined />,
};

const themeTooltips: Record<ThemeMode, string> = {
  light: siteChromeEn.theme.light,
  dark: siteChromeEn.theme.dark,
  system: siteChromeEn.theme.system,
};

const { Header: AntHeader } = Layout;

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { mode, cycleTheme } = useTheme();
  const persona = getPersona();
  const isPro = persona === 'pro';
  const appHomePath = isPro ? '/clients' : '/trips';
  const appHomeLabel = isPro ? siteChromeEn.nav.clients : siteChromeEn.nav.myTrips;

  const menuItems: MenuProps['items'] = [
    { key: '/', label: <Link to="/">{siteChromeEn.nav.home}</Link> },
    { key: '/about', label: <Link to="/about">{siteChromeEn.nav.about}</Link> },
    { key: '/contact', label: <Link to="/contact">{siteChromeEn.nav.contact}</Link> },
    { key: '/support', label: <Link to="/contact">{siteChromeEn.nav.support}</Link> },
    ...(isAuthenticated
      ? isPro
        ? [
            { key: '/clients', label: <Link to="/clients">{siteChromeEn.nav.clients}</Link> },
            { key: '/list-builder', label: <Link to="/list-builder">{siteChromeEn.nav.listBuilder}</Link> },
          ]
        : [{ key: '/trips', label: <Link to="/trips">{siteChromeEn.nav.myTrips}</Link> }]
      : []),
  ];

  const toggleMenu = (): void => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = (): void => {
    setMenuOpen(false);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <span>
          <UserOutlined /> {user?.email || user?.name || siteChromeEn.auth.userFallback}
        </span>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'settings',
      label: (
        <Link to="/settings">
          <SettingOutlined /> {siteChromeEn.auth.settings}
        </Link>
      ),
    },
    {
      key: 'logout',
      label: (
        <Link to="/logout">
          <LogoutOutlined /> {siteChromeEn.auth.logout}
        </Link>
      ),
    },
  ];

  return (
    <AntHeader className={styles.header}>
      <div className={styles.container}>
        <Link to="/" className={styles.logo}>
          <img
            src="/images/WandererWeatherlight.png"
            alt={siteChromeEn.productName}
            className={styles.logoImage}
          />
          <span className={styles.logoText}>{siteChromeEn.productName}</span>
        </Link>
        <div className={styles.nav}>
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={menuItems}
            className={styles.desktopMenu}
          />
          <div className={styles.authButtons}>
            <Tooltip title={themeTooltips[mode]}>
              <Button type="text" icon={themeIcons[mode]} onClick={cycleTheme} className={styles.themeToggle} aria-label={themeTooltips[mode]} />
            </Tooltip>
            {isAuthenticated ? (
              <>
                <Link to={appHomePath}>
                  <Button type="primary" className={styles.clientsButton}>
                    {appHomeLabel}
                  </Button>
                </Link>
                <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                  <Button type="text" icon={<UserOutlined />} className={styles.userButton}>
                    {user?.email || user?.name || siteChromeEn.auth.userFallback}
                  </Button>
                </Dropdown>
              </>
            ) : (
              <Link to="/signin">
                <Button type="primary" className={styles.userButton}>
                  {siteChromeEn.auth.signIn}
                </Button>
              </Link>
            )}
          </div>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={toggleMenu}
            className={styles.mobileMenuButton}
          />
        </div>
        {menuOpen && (
          <div className={styles.mobileMenu}>
            <div className={styles.mobileMenuContent}>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={closeMenu}
                className={styles.closeButton}
              />
              <Menu
                mode="vertical"
                selectedKeys={[location.pathname]}
                items={menuItems}
                onClick={closeMenu}
              />
              <div className={styles.mobileAuthButtons}>
                {isAuthenticated ? (
                  <Space direction="vertical" style={{ width: '100%', padding: '10px' }}>
                    <Link to={appHomePath} onClick={closeMenu}>
                      <Button type="primary" block>
                        {appHomeLabel}
                      </Button>
                    </Link>
                    <div style={{ padding: '0 12px', color: 'rgba(0, 0, 0, 0.65)' }}>
                      <UserOutlined /> {user?.email || user?.name || siteChromeEn.auth.userFallback}
                    </div>
                    <Link to="/settings" onClick={closeMenu}>
                      <Button block>
                        <SettingOutlined /> {siteChromeEn.auth.settings}
                      </Button>
                    </Link>
                    <Link to="/logout">
                      <Button block>
                        <LogoutOutlined /> {siteChromeEn.auth.logout}
                      </Button>
                    </Link>
                  </Space>
                ) : (
                  <Link to="/signin" onClick={closeMenu}>
                    <Button type="primary" block>
                      {siteChromeEn.auth.signIn}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            <div className={styles.menuOverlay} onClick={closeMenu}></div>
          </div>
        )}
      </div>
    </AntHeader>
  );
};

export default Header;
